import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaEdit, FaSave, FaTimes, FaPlus, FaTrash, FaDownload, FaSearch, FaExternalLinkAlt } from 'react-icons/fa';
import { getAllVouchers, createVoucher, updateVoucher, deleteVoucher } from '../../utils/vouchersApi';
import { showSuccess, showError } from '../../components/CustomAlert';
import { useConfirm } from '../../components/useConfirm';
import CustomConfirm from '../../components/CustomConfirm';
import { useSelector } from 'react-redux';
import { CourseSelect, SessionSelect } from '../../components/MasterDataSelect';
import { hasPermission } from '../../utils/permissions';
import { getSecureFileUrl, getSecureFileUrlWithToken } from '../../utils/secureFileAccess';

const Container = styled.div`
  padding: 30px;
  background: #f8f9fa;
  min-height: 100vh;
`;

const Header = styled.div`
  background: white;
  padding: 20px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
`;

const BreadcrumbNav = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 15px;
`;

const BreadcrumbItem = styled.span`
  &:after {
    content: '>';
    margin-left: 8px;
  }
  
  &:last-child:after {
    content: '';
  }
`;

const Title = styled.h2`
  color: #2c3e50;
  margin: 0;
  font-size: 1.5rem;
`;

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SearchInput = styled.input`
  padding: 10px 15px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  width: 300px;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
  }
`;

const AddButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgb(41, 56, 137);
    transform: translateY(-2px);
  }
`;

const VouchersContainer = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: #f8f9fa;
`;

const Th = styled.th`
  padding: 15px 20px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #e9ecef;
  font-size: 0.9rem;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #e9ecef;
  }
`;

const Td = styled.td`
  padding: 15px 20px;
  color: #495057;
  font-size: 0.95rem;
`;

const StudentInfo = styled.div`
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 10px;
`;

const StudentName = styled.div`
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
`;

const StudentDetails = styled.div`
  font-size: 0.9rem;
  color: #6c757d;
`;

const VoucherData = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
`;

const VoucherField = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldLabel = styled.label`
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 2px;
  font-weight: 500;
`;

const FieldValue = styled.div`
  font-size: 0.9rem;
  color: #495057;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #e1e5e9;
  border-radius: 4px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
  }
`;

const FileInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e1e5e9;
  border-radius: 4px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const EditButton = styled(Button)`
  background: #ffc107;
  color: #212529;
  
  &:hover {
    background: #e0a800;
  }
`;

const SaveButton = styled(Button)`
  background: #28a745;
  color: white;
  
  &:hover {
    background: #218838;
  }
`;

const CancelButton = styled(Button)`
  background: #6c757d;
  color: white;
  
  &:hover {
    background: #5a6268;
  }
`;

const DeleteButton = styled(Button)`
  background: #dc3545;
  color: white;
  
  &:hover {
    background: #c82333;
  }
`;

const ViewButton = styled(Button)`
  background: #17a2b8;
  color: white;
  
  &:hover {
    background: #138496;
  }
`;

const NoData = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
`;

const NoDataIcon = styled.div`
  font-size: 4rem;
  color: #fd7e14;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
`;

const NoDataTitle = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 15px;
`;

const NoDataMessage = styled.div`
  font-size: 1rem;
  color: #6c757d;
  margin-bottom: 25px;
  line-height: 1.5;
`;

const ClearSearchButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: #5a6268;
    transform: translateY(-1px);
  }
`;

const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 50px;
  color: #6c757d;
  font-size: 1.1rem;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid rgb(11, 23, 92);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Modal Styles
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e9ecef;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #2c3e50;
  font-size: 1.3rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  
  &:hover {
    color: #dc3545;
  }
`;

const ModalForm = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled.label`
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
  font-size: 0.9rem;
`;

const FormInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
  }
`;

const PhoneInputContainer = styled.div`
  display: flex;
  align-items: center;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.3s ease;
  
  &:focus-within {
    border-color: rgb(51, 66, 147);
  }
`;

const CountryCode = styled.div`
  background: #f8f9fa;
  padding: 12px 16px;
  border-right: 1px solid #e1e5e9;
  font-size: 16px;
  color: #495057;
  font-weight: 500;
`;

const PhoneInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: none;
  font-size: 16px;
  outline: none;
  
  &::placeholder {
    color: #adb5bd;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 4px;
`;

const FormFileInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
`;

const ModalButton = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const SubmitButton = styled(ModalButton)`
  background: rgb(51, 66, 147);
  color: white;
  
  &:hover {
    background: rgb(41, 56, 137);
  }
`;

const CancelModalButton = styled(ModalButton)`
  background: #6c757d;
  color: white;
  
  &:hover {
    background: #5a6268;
  }
`;

const AddVoucherButton = styled(Button)`
  background: rgb(51, 66, 147);
  color: white;
  
  &:hover {
    background: rgb(41, 56, 137);
  }
`;

const FilterSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 200px;
`;

const FilterLabel = styled.label`
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
  font-size: 0.9rem;
`;

const SearchButton = styled.button`
   border: none;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  transition: transform 0.2s ease;
  background: transparent;
  border: 1px solid #e9ecef;
  color: #2f2e2e;
  margin-top: 24px;

  &:hover {
    transform: translateY(-2px);
  }
`;

// One row per student; backend can return multiple rows per student (one per voucher).
const deduplicateByStudent = (rows) => {
  if (!Array.isArray(rows)) return [];
  const byStudent = new Map();
  for (const row of rows) {
    const id = row.student_id;
    const existing = byStudent.get(id);
    if (!existing) {
      byStudent.set(id, row);
    } else {
      const curHas = !!row.voucher_id;
      const exHas = !!existing.voucher_id;
      if (curHas && !exHas) byStudent.set(id, row);
      else if (curHas && exHas && (row.voucher_id ?? 0) > (existing.voucher_id ?? 0)) byStudent.set(id, row);
    }
  }
  return Array.from(byStudent.values());
};

const Vouchers = () => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [newVoucherData, setNewVoucherData] = useState({});
    const [phoneError, setPhoneError] = useState('');
    const { showConfirm } = useConfirm();

    // Add filter state
    const [filters, setFilters] = useState({
        course: '',
        session: ''
    });
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);

    // Get master data from Redux
    const { courses: masterCourses, sessions: masterSessions } = useSelector(state => state.masterData);
    const { token, user } = useSelector(state => state.auth);
    const canCreateStudents = hasPermission(user, 'students_create');
    const canUpdateStudents = hasPermission(user, 'students_update');
    const canDeleteStudents = hasPermission(user, 'students_delete');

    useEffect(() => {
        fetchStudents();
    }, []);

    useEffect(() => {
    }, [editData]);

    // Auto-search when filters change
    useEffect(() => {
        handleSearch();
    }, [filters]);

 

    const handleFilterChange = (field, value) => {
        setFilters({
            ...filters,
            [field]: value
        });
    };

    const handleSearch = () => {
       
        // If no filters are selected, show all students
        if (!filters.course && !filters.session) {
            setFilteredStudents([]);
            setIsFiltered(false);
            return;
        }

        let filtered = students;

        if (filters.course) {
            filtered = filtered.filter(student =>
                student.course === filters.course ||
                student.course?.toLowerCase() === filters.course.toLowerCase()
            );
        }

        if (filters.session) {
            // Find the session object for the selected session ID
            const selectedSession = masterSessions.find(s => String(s.id) === String(filters.session));
          

            filtered = filtered.filter(student => {
              
                // If student has no session data, skip them
                if (!student.session) {
                    return false;
                }

                // Try multiple matching strategies
                const studentSession = student.session;
                const sessionId = filters.session;
                const sessionName = selectedSession?.session_name;

                // Match by session ID
                if (String(studentSession) === String(sessionId)) {
                    return true;
                }

                // Match by session name
                if (sessionName && String(studentSession) === String(sessionName)) {
                    return true;
                }

                // Match by session name (case insensitive)
                if (sessionName && studentSession?.toLowerCase() === sessionName.toLowerCase()) {
                    return true;
                }

                // Match by session ID as string comparison
                if (studentSession && sessionId && studentSession.toString() === sessionId.toString()) {
                    return true;
                }

                return false;
            });
        }


        // Check if any students have session data
        const studentsWithSessions = students.filter(s => s.session);
       
        setFilteredStudents(filtered);
        setIsFiltered(true);
    };

    const handleClearFilters = () => {
        setFilters({
            course: '',
            session: ''
        });
        setFilteredStudents([]);
        setIsFiltered(false);
        // Also clear the text search when clearing filters
        setSearchTerm('');
    };

    const handleClearTextSearch = () => {
        setSearchTerm('');
    };

    // Get the students to display (filtered by course/session or all)
    const displayStudents = isFiltered ? filteredStudents : students;

    // Apply name search by full name only; no match => exclude (empty results when name doesn't exist)
    const textFilteredStudents = displayStudents.filter(student => {
        const trimmedSearchTerm = (searchTerm || '').trim();
        if (!trimmedSearchTerm) return true;

        const searchLower = trimmedSearchTerm.toLowerCase();
        const first = String(student.first_name || '').trim().toLowerCase();
        const last = String(student.last_name || '').trim().toLowerCase();
        const fullNameForward = `${first} ${last}`.trim();
        const fullNameReverse = `${last} ${first}`.trim();

        return (
            (first && first.includes(searchLower)) ||
            (last && last.includes(searchLower)) ||
            (fullNameForward.length > 0 && fullNameForward.includes(searchLower)) ||
            (fullNameReverse.length > 0 && fullNameReverse.includes(searchLower))
        );
    });

    const fetchStudents = async () => {
        try {
            setLoading(true);
            const response = await getAllVouchers();
            const raw = (response && response.data !== undefined) ? response.data : (Array.isArray(response) ? response : []);
            const list = Array.isArray(raw) ? raw : [];
            setStudents(deduplicateByStudent(list));
        } catch (error) {
            showError('Failed to fetch students');
            console.error('Error fetching students:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (student) => {

        // Format dates for HTML date inputs (YYYY-MM-DD)
        const formatDateForInput = (dateString) => {
            if (!dateString) return '';
            // If date is already in YYYY-MM-DD format, return as is
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                return dateString;
            }
            // If date is in a different format, convert to YYYY-MM-DD
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            return date.toISOString().split('T')[0];
        };
        const initialEditData = {
            studentId: student.student_id,
            referringWiaProvider: student.referring_wia_provider || '',
            caseManagerName: student.case_manager_name || '',
            caseManagerTelephone: student.case_manager_telephone || '',
            trainingProvider: student.training_provider || '',
            instructorName: student.instructor_name || '',
            programName: student.program_name || '',
            trainingPeriodStartDate: formatDateForInput(student.training_period_start_date),
            trainingPeriodEndDate: formatDateForInput(student.training_period_end_date),
            voucherFile: student.voucher_file_path || null
        };
        setEditingId(student.voucher_id);
        setEditData(initialEditData);
    };

    const handleSave = async (voucherId) => {
        // Validate phone number before saving
        if (editData.caseManagerTelephone) {
            const validation = validatePhoneNumber(editData.caseManagerTelephone);
            if (!validation.isValid) {
                setPhoneError(validation.error);
                return;
            }
        }

        try {
            // Ensure dates are in the correct format for the backend
            const dataToSend = {
                ...editData,
                trainingPeriodStartDate: editData.trainingPeriodStartDate || null,
                trainingPeriodEndDate: editData.trainingPeriodEndDate || null
            };

            await updateVoucher(voucherId, dataToSend);
            showSuccess('Voucher updated successfully');
            setEditingId(null);
            setEditData({});
            setPhoneError('');
            fetchStudents();
        } catch (error) {
            showError(error.message || 'Failed to save voucher');
        }
    };

    const handleCancel = () => {
        setEditingId(null);
        setEditData({});
    };

    const handleDelete = async (voucherId) => {
        const confirmed = await showConfirm('Are you sure you want to delete this voucher?');
        if (!confirmed) return;

        try {
            await deleteVoucher(voucherId);
            showSuccess('Voucher deleted successfully');
            fetchStudents();
        } catch (error) {
            showError(error.message || 'Failed to delete voucher');
        }
    };

    const handleView = (filePath) => {
        if (filePath) {
            const fullPath = buildFileUrl(filePath);
            window.open(fullPath, '_blank');
        }
    };

    // Helper function to build secure file URLs for voucher files
    const buildFileUrl = (filePath) => {
        if (!filePath) return '';

        let cleanedPath = filePath;

        // Remove the admin/backend/uploads/ prefix if it exists
        if (filePath.startsWith('admin/backend/uploads/')) {
            cleanedPath = filePath.replace('admin/backend/uploads/', '');
        }
        // If the path contains 'uploads/', extract only the part after it
        else if (filePath.includes('uploads/')) {
            const uploadsIndex = filePath.indexOf('uploads/');
            cleanedPath = filePath.substring(uploadsIndex + 'uploads/'.length);
        }

        // Additional cleaning: remove any remaining admin/backend/ prefixes
        if (cleanedPath.startsWith('admin/backend/')) {
            cleanedPath = cleanedPath.replace('admin/backend/', '');
        }

        // Use the secure file access function with the cleaned path
        return getSecureFileUrlWithToken(cleanedPath, token);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setEditData(prev => ({ ...prev, voucherFile: file }));
    };

    const handleInputChange = (field, value) => {
        setEditData(prev => {
            const updated = { ...prev, [field]: value };
            return updated;
        });
    };

    const handleAddVoucher = (student) => {
        setSelectedStudent(student);
        setNewVoucherData({
            studentId: student.student_id,
            referringWiaProvider: '',
            caseManagerName: '',
            caseManagerTelephone: '',
            trainingProvider: '',
            instructorName: '',
            programName: '',
            trainingPeriodStartDate: '',
            trainingPeriodEndDate: '',
            voucherFile: null
        });
        setShowModal(true);
    };
    const handleModalInputChange = (field, value) => {
        setNewVoucherData(prev => ({ ...prev, [field]: value }));
    };

    const validatePhoneNumber = (phone) => {
        // Remove all non-digits
        const digits = phone.replace(/\D/g, '');

        // Check if it's exactly 10 digits (US phone number)
        if (digits.length === 0) {
            return { isValid: true, error: '' }; // Allow empty
        }

        if (digits.length !== 10) {
            return { isValid: false, error: 'Phone number must be 10 digits' };
        }

        return { isValid: true, error: '' };
    };

    const formatPhoneNumber = (value) => {
        // Remove all non-digits
        const digits = value.replace(/\D/g, '');

        // Format as (XXX) XXX-XXXX
        if (digits.length >= 6) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
        } else if (digits.length >= 3) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        } else if (digits.length > 0) {
            return `(${digits}`;
        }

        return digits;
    };

    const handlePhoneChange = (value) => {
        const formatted = formatPhoneNumber(value);
        const validation = validatePhoneNumber(value);

        setNewVoucherData(prev => ({ ...prev, caseManagerTelephone: formatted }));
        setPhoneError(validation.error);
    };

    const handleModalFileChange = (e) => {
        const file = e.target.files[0];
        setNewVoucherData(prev => ({ ...prev, voucherFile: file }));
    };

    const handleSubmitVoucher = async (e) => {
        e.preventDefault();

        // Validate phone number before submission
        if (newVoucherData.caseManagerTelephone) {
            const validation = validatePhoneNumber(newVoucherData.caseManagerTelephone);
            if (!validation.isValid) {
                setPhoneError(validation.error);
                return;
            }
        }

        try {
            // Ensure dates are in the correct format for the backend
            const dataToSend = {
                ...newVoucherData,
                trainingPeriodStartDate: newVoucherData.trainingPeriodStartDate || null,
                trainingPeriodEndDate: newVoucherData.trainingPeriodEndDate || null
            };

            await createVoucher(dataToSend);
            showSuccess('Voucher created successfully');
            setShowModal(false);
            setSelectedStudent(null);
            setNewVoucherData({});
            setPhoneError('');
            fetchStudents();
        } catch (error) {
            showError(error.message || 'Failed to create voucher');
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedStudent(null);
        setNewVoucherData({});
    };

    // Format date for display (MM/DD/YYYY)
    const formatDateForDisplay = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const renderStudentInfo = (student) => (
        <StudentInfo>
            <StudentName>{student.first_name} {student.last_name}</StudentName>
            <StudentDetails>
                Email: {student.email} | Registration ID: {student.registration_number}
            </StudentDetails>
        </StudentInfo>
    );



    const renderVoucherData = (student, isEditing = false) => {

        // If student has no voucher, show "No voucher" message
        if (!student.voucher_id) {
            return (
                <VoucherData>
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px 0', color: '#6c757d' }}>
                        No voucher found for this student
                    </div>
                </VoucherData>
            );
        }

        return (
            <VoucherData>
                <VoucherField>
                    <FieldLabel>Referring WIA Provider</FieldLabel>
                    {isEditing ? (
                        <Input
                            value={editData.referringWiaProvider || ''}
                            onChange={(e) => {
                                handleInputChange('referringWiaProvider', e.target.value);
                            }}
                        />
                    ) : (
                        <FieldValue>{student.referring_wia_provider || 'N/A'}</FieldValue>
                    )}
                </VoucherField>

                <VoucherField>
                    <FieldLabel>Case Manager Name</FieldLabel>
                    {isEditing ? (
                        <Input
                            value={editData.caseManagerName || ''}
                            onChange={(e) => handleInputChange('caseManagerName', e.target.value)}
                        />
                    ) : (
                        <FieldValue>{student.case_manager_name || 'N/A'}</FieldValue>
                    )}
                </VoucherField>

                <VoucherField>
                    <FieldLabel>Case Manager Telephone</FieldLabel>
                    {isEditing ? (
                        <div>
                            <PhoneInputContainer>
                                <CountryCode>🇺🇸 +1</CountryCode>
                                <PhoneInput
                                    value={editData.caseManagerTelephone || ''}
                                    onChange={(e) => {
                                        const formatted = formatPhoneNumber(e.target.value);
                                        const validation = validatePhoneNumber(e.target.value);
                                        handleInputChange('caseManagerTelephone', formatted);
                                        setPhoneError(validation.error);
                                    }}
                                    type='tel'
                                    placeholder="(555) 123-4567"
                                    maxLength="14"
                                />
                            </PhoneInputContainer>
                            {phoneError && <ErrorMessage>{phoneError}</ErrorMessage>}
                        </div>
                    ) : (
                        <FieldValue>{student.case_manager_telephone || 'N/A'}</FieldValue>
                    )}
                </VoucherField>

                <VoucherField>
                    <FieldLabel>Training Provider</FieldLabel>
                    {isEditing ? (
                        <Input
                            value={editData.trainingProvider || ''}
                            onChange={(e) => handleInputChange('trainingProvider', e.target.value)}
                        />
                    ) : (
                        <FieldValue>{student.training_provider || 'N/A'}</FieldValue>
                    )}
                </VoucherField>

                <VoucherField>
                    <FieldLabel>Instructor Name</FieldLabel>
                    {isEditing ? (
                        <Input
                            value={editData.instructorName || ''}
                            onChange={(e) => handleInputChange('instructorName', e.target.value)}
                        />
                    ) : (
                        <FieldValue>{student.instructor_name || 'N/A'}</FieldValue>
                    )}
                </VoucherField>

                <VoucherField>
                    <FieldLabel>Program Name</FieldLabel>
                    {isEditing ? (
                        <Input
                            value={editData.programName || ''}
                            onChange={(e) => handleInputChange('programName', e.target.value)}
                        />
                    ) : (
                        <FieldValue>{student.program_name || 'N/A'}</FieldValue>
                    )}
                </VoucherField>

                <VoucherField>
                    <FieldLabel>Training Period Start Date</FieldLabel>
                    {isEditing ? (
                        <Input
                            type="date"
                            value={editData.trainingPeriodStartDate || ''}
                            onChange={(e) => handleInputChange('trainingPeriodStartDate', e.target.value)}
                            required
                        />
                    ) : (
                        <FieldValue>{formatDateForDisplay(student.training_period_start_date)}</FieldValue>
                    )}
                </VoucherField>

                <VoucherField>
                    <FieldLabel>Training Period End Date</FieldLabel>
                    {isEditing ? (
                        <Input
                            type="date"
                            value={editData.trainingPeriodEndDate || ''}
                            onChange={(e) => handleInputChange('trainingPeriodEndDate', e.target.value)}
                            required
                        />
                    ) : (
                        <FieldValue>{formatDateForDisplay(student.training_period_end_date)}</FieldValue>
                    )}
                </VoucherField>
                <VoucherField>
                    <FieldLabel>Voucher File</FieldLabel>
                    {isEditing ? (
                        <FileInput
                            type="file"
                            onChange={handleFileChange}
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        />
                    ) : (
                        <FieldValue>
                            {student.voucher_file_path ? (
                                <ViewButton onClick={() => handleView(student.voucher_file_path)}>
                                    <FaExternalLinkAlt /> View
                                </ViewButton>
                            ) : (
                                'No file uploaded'
                            )}
                        </FieldValue>
                    )}
                </VoucherField>
            </VoucherData>
        );
    };



    if (loading) {
        return (
            <Container>
                <Loading>
                    <LoadingSpinner />
                </Loading>
            </Container>
        );
    }

    return (
        <Container>
            <Title style={{ marginBottom: 20, marginTop: 30 }}>Students Vouchers</Title>

            <FilterSection>
                <FilterRow>
                    <FilterGroup>
                        <FilterLabel htmlFor="name">Search by name</FilterLabel>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <SearchInput
                                type="text"
                                placeholder="Search by full name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button
                                    onClick={handleClearTextSearch}
                                    style={{
                                        position: 'absolute',
                                        right: '10px',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#6c757d',
                                        fontSize: '16px',
                                        padding: '0',
                                        width: '20px',
                                        height: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    title="Clear search"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    </FilterGroup>

                    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end' }}>
                        <FilterGroup>
                            <FilterLabel htmlFor="course">Course *</FilterLabel>
                            <CourseSelect
                                value={filters.course}
                                onChange={(value) => handleFilterChange('course', value)}
                                placeholder="Select course"
                                isClearable={true}
                                style={{
                                    padding: '10px'
                                }}
                            />
                        </FilterGroup>

                        <FilterGroup>
                            <FilterLabel htmlFor="session">Session *</FilterLabel>
                            <SessionSelect
                                value={filters.session}
                                onChange={(value) => handleFilterChange('session', value)}
                                placeholder="Select session"
                                isClearable={true}
                                courseId={filters.course}
                                sessions={masterSessions}
                                style={{
                                    padding: '10px'
                                }}
                            />
                        </FilterGroup>

                        <SearchButton onClick={handleSearch}>
                            <FaSearch />
                            Search
                        </SearchButton>

                        {isFiltered && (
                            <SearchButton
                                onClick={handleClearFilters}
                                style={{ marginLeft: '10px' }}
                            >
                                <FaTimes />
                                Clear
                            </SearchButton>
                        )}
                    </div>
                </FilterRow>
            </FilterSection>

            <VouchersContainer>
                <Table style={{ display: 'inline-table' }}>
                    <Thead>
                        <tr>
                            <Th>Student Information</Th>
                            <Th>Voucher Data</Th>
                            <Th>Actions</Th>
                        </tr>
                    </Thead>
                    <Tbody>
                        {textFilteredStudents.length === 0 ? (
                            <tr>
                                <Td colSpan="3">
                                    <NoData>
                                        <NoDataIcon>📋</NoDataIcon>
                                        <NoDataTitle>
                                            {isFiltered ? 'No Students Found' : 'No Students Found'}
                                        </NoDataTitle>
                                        <NoDataMessage>
                                            No students match your search criteria. Try adjusting your search terms or filters.

                                        </NoDataMessage>
                                        {isFiltered && (
                                            <ClearSearchButton onClick={handleClearFilters}>
                                                Clear Search
                                            </ClearSearchButton>
                                        )}
                                    </NoData>
                                </Td>
                            </tr>
                        ) : (
                            textFilteredStudents.map(student => (
                                <Tr key={`${student.student_id}-${student.voucher_id ?? 'v'}`}>
                                    <Td>
                                        {renderStudentInfo(student)}
                                    </Td>
                                    <Td>
                                        {(() => {
                                            const isEditing = student.voucher_id !== null && editingId === student.voucher_id;

                                            return renderVoucherData(student, isEditing);
                                        })()}
                                    </Td>
                                    <Td>
                                        <ActionButtons>
                                            {student.voucher_id !== null && editingId === student.voucher_id ? (
                                                <>
                                                    {canUpdateStudents && (
                                                        <SaveButton onClick={() => handleSave(student.voucher_id)}>
                                                            <FaSave /> Save
                                                        </SaveButton>
                                                    )}
                                                    <CancelButton onClick={handleCancel}>
                                                        <FaTimes /> Cancel
                                                    </CancelButton>
                                                </>
                                            ) : student.voucher_id ? (
                                                <>
                                                    {canUpdateStudents && (
                                                        <EditButton onClick={() => handleEdit(student)}>
                                                            <FaEdit /> Edit
                                                        </EditButton>
                                                    )}
                                                    {canDeleteStudents && (
                                                        <DeleteButton onClick={() => handleDelete(student.voucher_id)}>
                                                            <FaTrash /> Delete
                                                        </DeleteButton>
                                                    )}
                                                </>
                                            ) : (
                                                canCreateStudents && (
                                                    <AddVoucherButton onClick={() => handleAddVoucher(student)}>
                                                        <FaPlus /> Add Voucher
                                                    </AddVoucherButton>
                                                )
                                            )}
                                        </ActionButtons>
                                    </Td>
                                </Tr>
                            ))
                        )}
                    </Tbody>
                </Table>
            </VouchersContainer>

            <CustomConfirm />

            {/* Add Voucher Modal */}
            {showModal && (
                <Modal>
                    <ModalContent>
                        <ModalHeader>
                            <ModalTitle>
                                Add Voucher for {selectedStudent?.first_name} {selectedStudent?.last_name}
                            </ModalTitle>
                            <CloseButton onClick={handleCloseModal}>
                                <FaTimes />
                            </CloseButton>
                        </ModalHeader>

                        <ModalForm onSubmit={handleSubmitVoucher}>
                            <FormGroup>
                                <FormLabel>Referring WIA Provider</FormLabel>
                                <FormInput
                                    type="text"
                                    value={newVoucherData.referringWiaProvider || ''}
                                    onChange={(e) => handleModalInputChange('referringWiaProvider', e.target.value)}
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel>Case Manager Name</FormLabel>
                                <FormInput
                                    type="text"
                                    value={newVoucherData.caseManagerName || ''}
                                    onChange={(e) => handleModalInputChange('caseManagerName', e.target.value)}
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel>Case Manager Telephone</FormLabel>
                                <PhoneInputContainer>
                                    <CountryCode>🇺🇸 +1</CountryCode>
                                    <PhoneInput
                                        type="tel"
                                        value={newVoucherData.caseManagerTelephone || ''}
                                        onChange={(e) => handlePhoneChange(e.target.value)}
                                        placeholder="(555) 123-4567"
                                        maxLength="14"
                                    />
                                </PhoneInputContainer>
                                {phoneError && <ErrorMessage>{phoneError}</ErrorMessage>}
                            </FormGroup>

                            <FormGroup>
                                <FormLabel>Training Provider</FormLabel>
                                <FormInput
                                    type="text"
                                    value={newVoucherData.trainingProvider || ''}
                                    onChange={(e) => handleModalInputChange('trainingProvider', e.target.value)}
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel>Instructor Name</FormLabel>
                                <FormInput
                                    type="text"
                                    value={newVoucherData.instructorName || ''}
                                    onChange={(e) => handleModalInputChange('instructorName', e.target.value)}
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel>Program Name</FormLabel>
                                <FormInput
                                    type="text"
                                    value={newVoucherData.programName || ''}
                                    onChange={(e) => handleModalInputChange('programName', e.target.value)}
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel>Training Period Start Date</FormLabel>
                                <FormInput
                                    type="date"
                                    value={newVoucherData.trainingPeriodStartDate || ''}
                                    onChange={(e) => handleModalInputChange('trainingPeriodStartDate', e.target.value)}
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel>Training Period End Date</FormLabel>
                                <FormInput
                                    type="date"
                                    value={newVoucherData.trainingPeriodEndDate || ''}
                                    onChange={(e) => handleModalInputChange('trainingPeriodEndDate', e.target.value)}
                                />
                            </FormGroup>

                            <FormGroup style={{ gridColumn: '1 / -1' }}>
                                <FormLabel>Voucher File</FormLabel>
                                <FormFileInput
                                    type="file"
                                    onChange={handleModalFileChange}
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                />
                            </FormGroup>

                            <ModalActions>
                                <CancelModalButton type="button" onClick={handleCloseModal}>
                                    Cancel
                                </CancelModalButton>
                                <SubmitButton type="submit">
                                    Add Voucher
                                </SubmitButton>
                            </ModalActions>
                        </ModalForm>
                    </ModalContent>
                </Modal>
            )}
        </Container>
    );
};

export default Vouchers;